import { useState, useEffect } from 'react';
import { fetchWithAuth } from '../utils/fetchWithAuth';
import { trackException } from '../services/appInsightsService';
import Button from './ui/Button';
import Input from './ui/Input';
import {
    RefreshCw, Database, Pencil, ChevronDown, ChevronRight,
    Zap, MessageSquare, Video, Radio, Users, Clock, Loader2,
    CheckCircle, AlertCircle, Eye, X, Check, Globe, Volume2, Ticket,
    Package
} from 'lucide-react';
import './PlansManagement.css';
import { ReadOnlyNote } from './ui/ReadOnlyFieldset';

// `class` is space-delimited, so a name like 'Meeting Pro' lowercased straight into a
// className silently becomes TWO classes and the intended rule never matches. Every name
// that feeds a class attribute in this file goes through here first.
export const classSlug = (name) => (name || '').toLowerCase().replace(/[^a-z0-9]+/g, '-');

// Tier order for sorting. Unified Plan Catalog tiers (~/.claude/plans/golden-napping-planet.md
// §0-1) come first; retired tiers stay listed alongside them — versioned activation means old
// and new tenant plan tiers can briefly coexist during the P1 backend migration, and
// getVisibleTiers() only shows a tier row when it actually has plans (see groupPlansByTier()).
const TIER_ORDER = ['free', 'meeting', 'meeting pro', 'webinar', 'unlimited', 'starter', 'standard', 'professional', 'advanced', 'business'];

// Human-readable tier names (unified + legacy aliases)
const TIER_DISPLAY_NAMES = {
    free: 'Free',
    meeting: 'Meeting',
    'meeting pro': 'Meeting Pro',
    webinar: 'Webinar',
    unlimited: 'Unlimited',
    // Legacy aliases (map to unified names)
    starter: 'Starter',
    standard: 'Standard',
    professional: 'Professional',
    advanced: 'Advanced',
    business: 'Business',
    pro: 'Professional',
    enterprise: 'Enterprise'
};

// Tier descriptions
const TIER_DESCRIPTIONS = {
    free: 'No usage — post-trial residual state, hidden from acquisition surfaces',
    meeting: 'Per-seat meeting translation with Meeting AI',
    'meeting pro': 'Meeting plan plus glossary and full PIM',
    webinar: 'Per-host webinar line with Q&A Basic and broadcast',
    unlimited: 'Internal testing and special customers — no limits',
    // Legacy aliases
    starter: 'Perfect for small teams and occasional use',
    standard: 'Enhanced features with text-to-speech',
    professional: 'Advanced features for growing organizations',
    advanced: 'High-capacity with extended language support',
    business: 'Enterprise-grade for large teams and events',
    pro: 'Advanced features for growing organizations',
    enterprise: 'Enterprise-grade (legacy)'
};

const FUNCTIONS_URL = process.env.REACT_APP_FUNCTIONS_URL
    || 'https://realtranslation-func-dev-eastus2.azurewebsites.net/api';

/** The audited admin write path. Plan edits go through here — never the legacy `/plans/{id}`
 *  route, which is unaudited, takes no If-Match and no reason, and is being retired. */
const PLATFORM_PLANS_PATH = '/platform/plans';

/** The server's REASON_REQUIRED bounds for an audited plan write. */
const REASON_MIN_LENGTH = 5;
const REASON_MAX_LENGTH = 500;

const VERSION_CONFLICT_MESSAGE = 'Someone else saved this plan first. '
    + 'The latest values have been reloaded — review them and apply your change again.';

const isReasonValid = (reason) => reason.length >= REASON_MIN_LENGTH
    && reason.length <= REASON_MAX_LENGTH;

/** Projects a stored plan document into the editable modal form. `hourPass` is deliberately
 *  absent: it is a retired v2 placeholder the server no longer accepts. */
const toPlanForm = (plan) => ({
    eventPass: plan.eventPass ? { ...plan.eventPass } : null,
    limits: plan.limits ? { ...plan.limits } : null,
    features: plan.features ? { qa: { ...plan.features.qa } } : null,
    active: plan.active
});

/** Exactly the document body an audited save sends, minus the reason. Doubling as the
 *  change-detection signature keeps "what we compare" and "what we send" from drifting apart. */
const toPlanWriteBody = (form) => ({
    limits: form.limits ?? null,
    features: form.features ?? null,
    eventPass: form.eventPass ?? null,
    active: form.active ?? true
});

const PlansManagement = ({ readOnly = false }) => {
    const [plans, setPlans] = useState([]);
    const [eventPassTiers, setEventPassTiers] = useState([]);
    // The tiers document's version, from the SAME GET that produced the rows above. PUT
    // /billing/passes/tiers is a conditional replace (428 without If-Match, 412 on a stale one),
    // so a save that does not carry this cannot land at all.
    const [eventPassEtag, setEventPassEtag] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingTiers, setIsLoadingTiers] = useState(false);
    const [status, setStatus] = useState({ type: '', message: '' });
    const [productTypeFilter, setProductTypeFilter] = useState('all');
    const [hourPackTiers, setHourPackTiers] = useState({});
    const [isLoadingHourPacks, setIsLoadingHourPacks] = useState(false);
    const [showHourPackSection, setShowHourPackSection] = useState(true);
    const [editingEventPass, setEditingEventPass] = useState(null); // index into eventPassTiers
    const [eventPassFormData, setEventPassFormData] = useState({});
    const [editingPlan, setEditingPlan] = useState(null);
    const [viewingPlan, setViewingPlan] = useState(null);
    const [planFormData, setPlanFormData] = useState({});
    // Everything the audited write needs, all taken from the same GET /platform/plans read:
    // the etag to save against, the signature of the values that read returned, and the reason.
    const [editEtag, setEditEtag] = useState(null);
    const [editBaseline, setEditBaseline] = useState(null);
    const [editReason, setEditReason] = useState('');
    const [editError, setEditError] = useState(null);
    const [isLoadingEditPlan, setIsLoadingEditPlan] = useState(false);
    const [showSeedConfirmModal, setShowSeedConfirmModal] = useState(false);
    const [isSeedingPlans, setIsSeedingPlans] = useState(false);
    const [showEventPassSection, setShowEventPassSection] = useState(true);
    const [expandedSections, setExpandedSections] = useState({
        eventPass: true,
        limits: true,
        qaFeatures: true
    });

    // Clear status after 5 seconds
    useEffect(() => {
        if (status.message) {
            const timer = setTimeout(() => setStatus({ type: '', message: '' }), 5000);
            return () => clearTimeout(timer);
        }
    }, [status]);

    const fetchPlans = async () => {
        setIsLoading(true);
        try {
            const filterParam = productTypeFilter !== 'all' ? `?productType=${productTypeFilter}` : '';
            const response = await fetchWithAuth(
                `${FUNCTIONS_URL}/plans${filterParam}`,
                { method: 'GET' }
            );
            if (response.ok) {
                const data = await response.json();
                setPlans(data.plans || []);
            } else {
                throw new Error('Failed to fetch plans');
            }
        } catch (error) {
            setStatus({ type: 'error', message: error.message });
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchPlans();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [productTypeFilter]);

    const fetchEventPassTiers = async () => {
        setIsLoadingTiers(true);
        try {
            const response = await fetchWithAuth(
                `${FUNCTIONS_URL}/billing/passes/tiers`,
                { method: 'GET' }
            );
            if (response.ok) {
                const data = await response.json();
                setEventPassTiers(data.tiers || []);
                // Null when the catalog is still the compiled defaults rather than a stored
                // document — there is no version to be conditional on in that case.
                setEventPassEtag(data.etag || null);
            } else {
                throw new Error('Failed to fetch event pass tiers');
            }
        } catch (error) {
            console.error('Error fetching event pass tiers:', error);
            trackException(error, { errorType: 'PlansManagement_FetchEventPassTiersFailed' });
        } finally {
            setIsLoadingTiers(false);
        }
    };

    useEffect(() => {
        fetchEventPassTiers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchHourPackTiers = async () => {
        setIsLoadingHourPacks(true);
        try {
            const response = await fetchWithAuth(
                `${FUNCTIONS_URL}/billing/hour-packs`,
                { method: 'GET' }
            );
            if (response.ok) {
                const data = await response.json();
                setHourPackTiers(Array.isArray(data.packs) ? { 'Usage packs (all plans)': data.packs } : {});
            }
        } catch (error) {
            console.error('Error fetching hour pack tiers:', error);
            trackException(error, { errorType: 'PlansManagement_FetchHourPacksFailed' });
        } finally {
            setIsLoadingHourPacks(false);
        }
    };

    useEffect(() => {
        fetchHourPackTiers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const openEventPassEdit = (index, tier) => {
        setEditingEventPass(index);
        setEventPassFormData({ ...tier });
    };

    const handleSaveEventPass = async () => {
        if (readOnly) return;
        if (editingEventPass === null) return;

        // Build updated tiers array
        const updatedTiers = [...eventPassTiers];
        updatedTiers[editingEventPass] = {
            ...eventPassFormData,
            priceDisplay: `$${(eventPassFormData.priceCents / 100).toFixed(2)}`
        };

        try {
            // Exactly one precondition, chosen by what OUR OWN GET returned. An etag means a stored
            // catalog to replace conditionally; no etag means the environment is still on the compiled
            // defaults and this save authors the first stored catalog. `If-None-Match: *` is the HTTP
            // guard for that second case — the server creates only if the document really is absent, so
            // two admins who both saw "no catalog" cannot both believe they authored it. Sending both
            // headers is ambiguous and the server refuses it.
            const headers = { 'Content-Type': 'application/json' };
            if (eventPassEtag) {
                headers['If-Match'] = eventPassEtag;
            } else {
                headers['If-None-Match'] = '*';
            }

            const response = await fetchWithAuth(
                `${FUNCTIONS_URL}/billing/passes/tiers`,
                {
                    method: 'PUT',
                    headers,
                    body: JSON.stringify({ tiers: updatedTiers })
                }
            );
            if (response.ok) {
                setEventPassTiers(updatedTiers);
                setEditingEventPass(null);
                setEventPassFormData({});
                setStatus({ type: 'success', message: 'Event pass tier updated successfully' });
                // The version rotated with that write. Without re-reading it, the SECOND save in the
                // same session would send the consumed etag and 412.
                await fetchEventPassTiers();
            } else {
                const body = await response.json().catch(() => ({}));
                if (response.status === 412 || response.status === 428) {
                    // A stale or missing precondition guarantees a repeat failure — reload first.
                    await fetchEventPassTiers();
                    throw new Error(body.message
                        || 'Someone else created or changed these tiers first. The latest values have been reloaded — reapply your change.');
                }
                throw new Error(body.message || (await response.text().catch(() => `HTTP ${response.status}`)));
            }
        } catch (error) {
            setStatus({ type: 'error', message: `Failed to update event pass tier: ${error.message}` });
            trackException(error, { errorType: 'PlansManagement_UpdateEventPassTierFailed' });
        }
    };

    /** Seeds the edit form, its change-detection baseline and the etag to save against — all
     *  three from ONE read of the audited path. Reading the values from the already-loaded
     *  `/plans` list and the etag from here would let another admin's save slip between the two:
     *  stale values carried by a fresh etag pass the server's conflict check and silently
     *  revert their edit. */
    const applyLoadedPlan = (planDocument) => {
        const form = toPlanForm(planDocument);
        setPlanFormData(form);
        setEditBaseline(JSON.stringify(toPlanWriteBody(form)));
        setEditEtag(planDocument.etag || null);
    };

    const loadPlanForEdit = async (planId) => {
        setIsLoadingEditPlan(true);
        try {
            const response = await fetchWithAuth(
                `${FUNCTIONS_URL}${PLATFORM_PLANS_PATH}`,
                { method: 'GET' }
            );
            if (!response.ok) {
                throw new Error(`Failed to load the plan for editing (HTTP ${response.status}).`);
            }
            const data = await response.json();
            const catalog = Array.isArray(data) ? data : (data.plans || []);
            const planDocument = catalog.find(candidate => candidate.id === planId);
            if (!planDocument) {
                throw new Error('This plan is no longer in the catalog. Refresh the list and try again.');
            }
            applyLoadedPlan(planDocument);
        } catch (error) {
            trackException(error, { errorType: 'PlansManagement_LoadPlanForEditFailed' });
            setEditError(error.message);
        } finally {
            setIsLoadingEditPlan(false);
        }
    };

    const planHasChanges = () => editBaseline !== null
        && editBaseline !== JSON.stringify(toPlanWriteBody(planFormData));

    // A save that changes nothing carries no reason — that is the server's own rule, and
    // demanding one would block an admin re-saving after a conflict reload.
    const canSavePlan = () => !planHasChanges() || isReasonValid(editReason.trim());

    const handleUpdatePlan = async () => {
        if (readOnly) return;
        if (!editingPlan || !canSavePlan()) return;
        const reason = editReason.trim();
        setEditError(null);
        try {
            const headers = { 'Content-Type': 'application/json' };
            if (editEtag) headers['If-Match'] = editEtag;
            const body = toPlanWriteBody(planFormData);
            if (reason) body.reason = reason;

            const response = await fetchWithAuth(
                `${FUNCTIONS_URL}${PLATFORM_PLANS_PATH}/${encodeURIComponent(editingPlan.id)}`,
                { method: 'PUT', headers, body: JSON.stringify(body) }
            );

            if (response.status === 412) {
                // A stale precondition guarantees a repeat failure — the modal stays open on
                // the reloaded values so the admin can decide what to re-apply.
                setEditError(VERSION_CONFLICT_MESSAGE);
                trackException(new Error(VERSION_CONFLICT_MESSAGE), {
                    errorType: 'PlansManagement_UpdatePlanVersionConflict', planId: editingPlan.id
                });
                await fetchPlans();
                await loadPlanForEdit(editingPlan.id);
                return;
            }

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.message || errorData.error || 'Failed to update plan');
            }

            applyLoadedPlan(await response.json());
            setEditReason('');
            setStatus({ type: 'success', message: 'Plan updated successfully' });
            fetchPlans();
        } catch (error) {
            trackException(error, {
                errorType: 'PlansManagement_UpdatePlanFailed', planId: editingPlan.id
            });
            setEditError(error.message);
        }
    };

    const handleSeedPlans = async () => {
        if (readOnly) return;
        setIsSeedingPlans(true);
        try {
            const response = await fetchWithAuth(
                `${FUNCTIONS_URL}/plans/seed`,
                { method: 'POST' }
            );
            if (response.ok) {
                const data = await response.json();
                setStatus({ type: 'success', message: `Seeded ${data.seeded} plans successfully` });
                setShowSeedConfirmModal(false);
                fetchPlans();
            } else {
                throw new Error('Failed to seed plans');
            }
        } catch (error) {
            setStatus({ type: 'error', message: error.message });
        } finally {
            setIsSeedingPlans(false);
        }
    };

    const openEditModal = (plan) => {
        setEditingPlan(plan);
        // Deliberately NOT seeded from the already-loaded `/plans` list — every editable value
        // comes from the audited read below, alongside the etag it must be saved against.
        setPlanFormData({});
        setEditBaseline(null);
        setEditEtag(null);
        setEditReason('');
        setEditError(null);
        loadPlanForEdit(plan.id);
    };

    const toggleSection = (section) => {
        setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
    };

    const formatPrice = (cents) => {
        if (cents === null || cents === undefined) return null;
        if (cents === 0) return 'Free';
        // .99 price points are real money: rounding to whole dollars misstates every seat.
        return cents % 100 === 0 ? `$${cents / 100}` : `$${(cents / 100).toFixed(2)}`;
    };

    // Normalize tier name — use displayTier when available, fall back to tier, handle legacy mappings
    const normalizeTier = (plan) => {
        const raw = (plan.displayTier || plan.tier || '').toLowerCase();
        // Map legacy tier names to unified names
        if (raw === 'pro') return 'professional';
        if (raw === 'enterprise') return 'business';
        // Map Zoom tier names to their display tier
        if (raw.startsWith('zoommeetingt') || raw.startsWith('zoomwebinart')) {
            // Zoom v1 tiers: t1-t2 → starter, t3-t4 → professional, t5 → business
            if (raw.endsWith('t1') || raw.endsWith('t2')) return 'starter';
            if (raw.endsWith('t3') || raw.endsWith('t4')) return 'professional';
            if (raw.endsWith('t5')) return 'business';
        }
        return raw;
    };


    const groupPlansByTier = () => {
        const grouped = {};

        // Initialize all tiers — 'all' slot holds plans with productType='all' (e.g. Unlimited)
        TIER_ORDER.forEach(tier => {
            grouped[tier] = { all: null, meeting: null, webinar: null };
        });

        // One catalog, no channel slices: every plan lands in its tier's product slot.

        // Populate with actual plans
        plans.forEach(plan => {
            const tier = normalizeTier(plan);
            if (!grouped[tier]) return;

            // Plans with productType='all' get their own slot (single card, not duplicated)
            if (plan.productType === 'all') {
                grouped[tier].all = plan;
                return;
            }

            const productType = plan.productType || 'meeting';

            if (productType === 'webinar') {
                grouped[tier].webinar = plan;
            } else {
                grouped[tier].meeting = plan;
            }
        });

        return grouped;
    };

    const getPlanDisplayName = (plan) => {
        // Use plan.name if available (set during seeding), otherwise fall back to the tier name
        // alone — concatenating a product name here used to produce "Meeting Meeting".
        if (plan.name) return plan.name;
        const tier = normalizeTier(plan);
        return TIER_DISPLAY_NAMES[tier] || plan.tier;
    };

    const groupedPlans = groupPlansByTier();

    // Count plans in a tier given the current product filter
    const countTierPlans = (tierData) => {
        let count = 0;
        // 'all' plans (e.g. Unlimited) are always visible regardless of product filter
        if (tierData.all) count++;
        const showMeeting = productTypeFilter === 'all' || productTypeFilter === 'meeting';
        const showWebinar = productTypeFilter === 'all' || productTypeFilter === 'webinar';
        if (showMeeting) {
            if (tierData.meeting) count++;
        }
        if (showWebinar) {
            if (tierData.webinar) count++;
        }
        return count;
    };

    // Filter tiers based on product type filter
    const getVisibleTiers = () => {
        return TIER_ORDER.filter(tier => {
            const tierData = groupedPlans[tier];
            if (!tierData) return false;
            return countTierPlans(tierData) > 0;
        });
    };

    const visibleTiers = getVisibleTiers();

    return (
        <div className="plans-management">
            <ReadOnlyNote readOnly={readOnly} />
            {/* Toolbar */}
            <div className="plans-toolbar">
                <div className="plans-toolbar-left">
                    <select
                        className="ui-select product-filter"
                        aria-label="Filter plans by product type"
                        value={productTypeFilter}
                        onChange={(e) => setProductTypeFilter(e.target.value)}
                    >
                        <option value="all">All Products</option>
                        <option value="meeting">Meetings</option>
                        <option value="webinar">Webinars</option>
                    </select>

                </div>
                <div className="plans-toolbar-right">
                    {!readOnly && (
                    <Button
                        variant="secondary"
                        onClick={() => setShowSeedConfirmModal(true)}
                        className="seed-plans-btn"
                    >
                        <Database size={16} /> Seed Default Plans
                    </Button>
                    )}
                    <button
                        className="refresh-icon-btn"
                        onClick={fetchPlans}
                        disabled={isLoading}
                        title="Refresh plans list"
                    >
                        <RefreshCw size={20} className={isLoading ? 'rotating' : ''} />
                    </button>
                </div>
            </div>

            {/* Status Message */}
            {status.message && (
                <div className={`plans-status-message ${status.type}`}>
                    {status.type === 'error' ? <AlertCircle size={18} /> : <CheckCircle size={18} />}
                    {status.message}
                </div>
            )}

            {/* Loading State */}
            {isLoading ? (
                <div className="plans-loading">
                    <Loader2 className="animate-spin" size={40} />
                    <p>Loading plans...</p>
                </div>
            ) : plans.length === 0 ? (
                <div className="plans-empty">
                    <Database size={48} strokeWidth={1.5} />
                    <h3>No Plans Found</h3>
                    <p>Click "Seed Default Plans" to create initial pricing plans.</p>
                </div>
            ) : (
                /* Tier Groups */
                <div className="tier-groups">
                    {visibleTiers.map(tier => {
                        const tierData = groupedPlans[tier];
                        const planCount = countTierPlans(tierData);
                        const showMeeting = productTypeFilter === 'all' || productTypeFilter === 'meeting';
                        const showWebinar = productTypeFilter === 'all' || productTypeFilter === 'webinar';

                        return (
                            <div key={tier} className={`tier-group tier-${classSlug(tier)}`}>
                                <div className="tier-header">
                                    <div className="tier-header-left">
                                        <span className={`tier-label tier-label-${classSlug(tier)}`}>
                                            {TIER_DISPLAY_NAMES[tier]}
                                        </span>
                                        <span className="tier-description">
                                            {TIER_DESCRIPTIONS[tier]}
                                        </span>
                                    </div>
                                    <span className="tier-plan-count">
                                        {planCount} {planCount === 1 ? 'plan' : 'plans'}
                                    </span>
                                </div>

                                <div className="tier-cards">
                                    {/* All-product card (e.g. Unlimited) — single card spanning both types */}
                                    {tierData.all && (
                                        <PlanCard
                                            plan={tierData.all}
                                            productType="all"
                                            tier={tier}
                                            onEdit={readOnly ? null : openEditModal}
                                            onView={setViewingPlan}
                                            formatPrice={formatPrice}
                                            getPlanDisplayName={getPlanDisplayName}
                                        />
                                    )}

                                    {/* Direct/Free Meeting Card */}
                                    {showMeeting && tierData.meeting && (
                                        <PlanCard
                                            plan={tierData.meeting}
                                            productType="meeting"
                                            tier={tier}
                                            onEdit={readOnly ? null : openEditModal}
                                            onView={setViewingPlan}
                                            formatPrice={formatPrice}
                                            getPlanDisplayName={getPlanDisplayName}
                                        />
                                    )}

                                    {/* Direct/Free/Unlimited Webinar Card */}
                                    {showWebinar && tierData.webinar && (
                                        <PlanCard
                                            plan={tierData.webinar}
                                            productType="webinar"
                                            tier={tier}
                                            onEdit={readOnly ? null : openEditModal}
                                            onView={setViewingPlan}
                                            formatPrice={formatPrice}
                                            getPlanDisplayName={getPlanDisplayName}
                                        />
                                    )}

                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Event Pass Tiers Section */}
            {showEventPassSection && (productTypeFilter === 'all' || productTypeFilter === 'webinar') && (
                <div className="event-pass-section">
                    <div className="event-pass-header">
                        <div className="event-pass-header-left">
                            <Ticket size={20} />
                            <h3>Event Pass Tiers</h3>
                            <span className="event-pass-subtitle">Webinar event passes for large-scale events</span>
                        </div>
                        <button
                            className="collapse-btn"
                            onClick={() => setShowEventPassSection(prev => !prev)}
                            title="Toggle section"
                        >
                            <ChevronDown size={18} />
                        </button>
                    </div>

                    {isLoadingTiers ? (
                        <div className="event-pass-loading">
                            <Loader2 className="animate-spin" size={24} />
                            <span>Loading tiers...</span>
                        </div>
                    ) : (
                        <div className="event-pass-tiers-grid">
                            {eventPassTiers.map((tier, idx) => (
                                <div key={tier.name} className={`event-pass-tier-card tier-card-${classSlug(tier.name)}`}>
                                    <div className="tier-card-header">
                                        <div className="tier-card-header-left">
                                            <h4 className="tier-card-name">{tier.name}</h4>
                                            <span className="tier-card-price">{tier.priceDisplay}</span>
                                        </div>
                                        {!readOnly && (
                                        <button
                                            className="event-pass-edit-btn"
                                            onClick={() => openEventPassEdit(idx, tier)}
                                            title="Edit tier pricing"
                                        >
                                            <Pencil size={13} />
                                        </button>
                                        )}
                                    </div>
                                    <div className="tier-card-features">
                                        <div className="tier-feature">
                                            <Clock size={14} />
                                            <span>{Math.floor(tier.maxMinutes / 60)}h {tier.maxMinutes % 60 > 0 ? `${tier.maxMinutes % 60}m` : ''}</span>
                                        </div>
                                        <div className="tier-feature">
                                            <Users size={14} />
                                            <span>{tier.maxAttendees} attendees</span>
                                        </div>
                                        <div className="tier-feature">
                                            <Globe size={14} />
                                            <span>{tier.maxLanguages >= 60 ? 'All languages' : `${tier.maxLanguages} languages`}</span>
                                        </div>
                                        <div className="tier-feature">
                                            <Volume2 size={14} />
                                            <span>{tier.ttsIncluded ? 'TTS included' : 'No TTS'}</span>
                                        </div>
                                        <div className="tier-feature">
                                            <MessageSquare size={14} />
                                            <span>Q&A {tier.qaTier}</span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Hour Pack Tiers Section */}
            {(productTypeFilter === 'all' || productTypeFilter === 'meeting') && (
                <div className="hour-pack-section">
                    <div className="hour-pack-header">
                        <div className="hour-pack-header-left">
                            <Package size={20} />
                            <h3>Usage Pack Add-ons</h3>
                            <span className="hour-pack-subtitle">Extra points at one price for every plan — no multipliers</span>
                        </div>
                        <button
                            className="collapse-btn"
                            onClick={() => setShowHourPackSection(prev => !prev)}
                            title={showHourPackSection ? 'Collapse section' : 'Expand section'}
                        >
                            {showHourPackSection ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                        </button>
                    </div>

                    {showHourPackSection && (isLoadingHourPacks ? (
                        <div className="hour-pack-loading">
                            <Loader2 className="animate-spin" size={24} />
                            <span>Loading usage packs...</span>
                        </div>
                    ) : Object.keys(hourPackTiers).length === 0 ? (
                        <div className="hour-pack-empty">
                            <p>No usage pack data available. The <code>GET /api/billing/hour-packs</code> endpoint lists nothing while usage-pack sales are switched off, and may also not be deployed yet.</p>
                        </div>
                    ) : (
                        <div className="hour-pack-tiers">
                            {Object.entries(hourPackTiers).map(([tierName, packs]) => (
                                <div key={tierName} className="hour-pack-tier-group">
                                    <h4 className="hour-pack-tier-name">{tierName}</h4>
                                    <div className="hour-pack-cards">
                                        {packs.map(pack => (
                                            <div key={pack.id} className="hour-pack-card">
                                                <span className="hour-pack-name">{pack.name}</span>
                                                <span className="hour-pack-price">${(pack.priceCents / 100).toFixed(2)}</span>
                                                {/* The quantity comes from `points`, the only size this
                                                    route publishes. Rendered as a JSX child rather than
                                                    an interpolated string so a response without the
                                                    field prints nothing at all instead of the word
                                                    "undefined". */}
                                                <span className="hour-pack-points">{pack.points} points</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    ))}
                </div>
            )}

            {/* Edit Event Pass Tier Modal */}
            {editingEventPass !== null && (
                <div className="modal-overlay" onClick={() => setEditingEventPass(null)}>
                    <div className="plans-modal" onClick={e => e.stopPropagation()}>
                        <h3>Edit Event Pass Tier</h3>
                        <div className="plan-form-sections">
                            <div className="form-row">
                                <label htmlFor="event-pass-name">Name</label>
                                <Input
                                    id="event-pass-name"
                                    type="text"
                                    value={eventPassFormData.name || ''}
                                    onChange={(e) => setEventPassFormData(prev => ({ ...prev, name: e.target.value }))}
                                />
                            </div>
                            <div className="form-row">
                                <label htmlFor="event-pass-price">Price ($)</label>
                                <div className="price-input-wrapper">
                                    <span className="price-prefix">$</span>
                                    <Input
                                        id="event-pass-price"
                                        type="number"
                                        value={(eventPassFormData.priceCents || 0) / 100}
                                        onChange={(e) => setEventPassFormData(prev => ({ ...prev, priceCents: Math.round(parseFloat(e.target.value) * 100) }))}
                                    />
                                </div>
                            </div>
                            <div className="form-row">
                                <label htmlFor="event-pass-max-minutes">Max Minutes</label>
                                <Input
                                    id="event-pass-max-minutes"
                                    type="number"
                                    value={eventPassFormData.maxMinutes || 0}
                                    onChange={(e) => setEventPassFormData(prev => ({ ...prev, maxMinutes: parseInt(e.target.value) || 0 }))}
                                />
                            </div>
                            <div className="form-row">
                                <label htmlFor="event-pass-max-attendees">Max Attendees</label>
                                <Input
                                    id="event-pass-max-attendees"
                                    type="number"
                                    value={eventPassFormData.maxAttendees || 0}
                                    onChange={(e) => setEventPassFormData(prev => ({ ...prev, maxAttendees: parseInt(e.target.value) || 0 }))}
                                />
                            </div>
                            <div className="form-row">
                                <label htmlFor="event-pass-max-languages">Max Languages</label>
                                <Input
                                    id="event-pass-max-languages"
                                    type="number"
                                    value={eventPassFormData.maxLanguages || 0}
                                    onChange={(e) => setEventPassFormData(prev => ({ ...prev, maxLanguages: parseInt(e.target.value) || 0 }))}
                                />
                            </div>
                            <div className="form-row checkbox-row">
                                <label>
                                    <input
                                        type="checkbox"
                                        checked={eventPassFormData.ttsIncluded || false}
                                        onChange={(e) => setEventPassFormData(prev => ({ ...prev, ttsIncluded: e.target.checked }))}
                                    />
                                    TTS Included
                                </label>
                            </div>
                            <div className="form-row">
                                <label htmlFor="event-pass-qa-tier">Q&A Tier</label>
                                <select
                                    id="event-pass-qa-tier"
                                    className="ui-select"
                                    value={eventPassFormData.qaTier || 'Basic'}
                                    onChange={(e) => setEventPassFormData(prev => ({ ...prev, qaTier: e.target.value }))}
                                >
                                    <option value="Basic">Basic</option>
                                    <option value="Pro">Pro</option>
                                </select>
                            </div>
                        </div>
                        <div className="modal-actions">
                            <Button variant="ghost" onClick={() => setEditingEventPass(null)}>Cancel</Button>
                            <Button variant="primary" onClick={handleSaveEventPass}>Save</Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Seed Plans Confirmation Modal */}
            {showSeedConfirmModal && (
                <div className="modal-overlay" onClick={() => setShowSeedConfirmModal(false)}>
                    <div className="plans-modal" onClick={e => e.stopPropagation()}>
                        <h3>Seed Default Plans</h3>
                        <p>This will create default pricing plans for meetings and webinars. Existing plans will not be overwritten.</p>
                        <div className="modal-actions">
                            <Button variant="ghost" onClick={() => setShowSeedConfirmModal(false)}>
                                Cancel
                            </Button>
                            <Button
                                variant="primary"
                                onClick={handleSeedPlans}
                                disabled={isSeedingPlans}
                            >
                                {isSeedingPlans ? (
                                    <><Loader2 size={16} className="animate-spin" /> Seeding...</>
                                ) : (
                                    'Confirm Seed'
                                )}
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Plan Modal */}
            {editingPlan && (
                <div className="modal-overlay" onClick={() => setEditingPlan(null)}>
                    <div className="edit-plan-modal" onClick={e => e.stopPropagation()}>
                        <div className="edit-plan-header">
                            <h3>Edit Plan</h3>
                            <div className="edit-plan-badges">
                                <span className={`tier-label tier-label-${classSlug(editingPlan.tier)}`}>
                                    {editingPlan.tier}
                                </span>
                                <span className={`product-badge product-${editingPlan.productType}`}>
                                    {editingPlan.productType === 'webinar' ? <Radio size={14} /> : <Video size={14} />}
                                    {editingPlan.productType}
                                </span>
                            </div>
                        </div>
                        <p className="plan-id-display">ID: <code>{editingPlan.id}</code></p>

                        {editError && (
                            <div className="plans-status-message error edit-plan-error" role="alert">
                                <AlertCircle size={18} />
                                {editError}
                            </div>
                        )}

                        {/* aria-live: the modal opens with stale-looking fields while /platform/plans is
                            in flight. A sighted admin sees this line; without a live region a screen-reader
                            user is never told the values are still settling and may edit the old ones. */}
                        <div aria-live="polite">
                            {isLoadingEditPlan && (
                                <p className="edit-plan-loading">Loading the latest saved values...</p>
                            )}
                        </div>

                        <div className="plan-form-sections">
                            {/* Event Pass Section (Webinar only) */}
                            {editingPlan.productType === 'webinar' && (
                                <div className="plan-section">
                                    <button
                                        className="section-header"
                                        onClick={() => toggleSection('eventPass')}
                                    >
                                        <Zap size={18} />
                                        <span>Event Pass</span>
                                        {expandedSections.eventPass ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                                    </button>
                                    {expandedSections.eventPass && planFormData.eventPass && (
                                        <div className="section-content">
                                            <div className="form-row">
                                                <label htmlFor="plan-event-pass-price">Price (cents)</label>
                                                <div className="price-input-wrapper">
                                                    <span className="price-prefix">$</span>
                                                    <Input
                                                        id="plan-event-pass-price"
                                                        type="number"
                                                        value={planFormData.eventPass.priceCents / 100 || 0}
                                                        onChange={(e) => setPlanFormData(prev => ({
                                                            ...prev,
                                                            eventPass: { ...prev.eventPass, priceCents: Math.round(parseFloat(e.target.value) * 100) }
                                                        }))}
                                                    />
                                                </div>
                                            </div>
                                            <div className="form-row">
                                                <label htmlFor="plan-event-pass-duration">Duration (minutes)</label>
                                                <Input
                                                    id="plan-event-pass-duration"
                                                    type="number"
                                                    value={planFormData.eventPass.durationMinutes || 180}
                                                    onChange={(e) => setPlanFormData(prev => ({
                                                        ...prev,
                                                        eventPass: { ...prev.eventPass, durationMinutes: parseInt(e.target.value) }
                                                    }))}
                                                />
                                            </div>
                                            <div className="form-row">
                                                <label htmlFor="plan-event-pass-max-participants">Max Participants</label>
                                                <Input
                                                    id="plan-event-pass-max-participants"
                                                    type="number"
                                                    value={planFormData.eventPass.maxParticipants || 500}
                                                    onChange={(e) => setPlanFormData(prev => ({
                                                        ...prev,
                                                        eventPass: { ...prev.eventPass, maxParticipants: parseInt(e.target.value) }
                                                    }))}
                                                />
                                            </div>
                                            <div className="form-row checkbox-row">
                                                <label>
                                                    <input
                                                        type="checkbox"
                                                        checked={planFormData.eventPass.qaIncluded || false}
                                                        onChange={(e) => setPlanFormData(prev => ({
                                                            ...prev,
                                                            eventPass: { ...prev.eventPass, qaIncluded: e.target.checked }
                                                        }))}
                                                    />
                                                    Q&A Included
                                                </label>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Q&A Limits Section (Webinar only) */}
                            {editingPlan.productType === 'webinar' && (
                                <div className="plan-section">
                                    <button
                                        className="section-header"
                                        onClick={() => toggleSection('limits')}
                                    >
                                        <Database size={18} />
                                        <span>Q&A Limits</span>
                                        {expandedSections.limits ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                                    </button>
                                    {expandedSections.limits && planFormData.limits && (
                                        <div className="section-content">
                                            <div className="form-row">
                                                <label htmlFor="plan-limits-max-questions">Max Questions/Session (-1 = unlimited)</label>
                                                <Input
                                                    id="plan-limits-max-questions"
                                                    type="number"
                                                    value={planFormData.limits.maxQuestionsPerSession ?? -1}
                                                    onChange={(e) => setPlanFormData(prev => ({
                                                        ...prev,
                                                        limits: { ...prev.limits, maxQuestionsPerSession: parseInt(e.target.value) }
                                                    }))}
                                                />
                                            </div>
                                            <div className="form-row">
                                                <label htmlFor="plan-limits-max-replies">Max Replies/Question</label>
                                                <Input
                                                    id="plan-limits-max-replies"
                                                    type="number"
                                                    value={planFormData.limits.maxRepliesPerQuestion ?? 10}
                                                    onChange={(e) => setPlanFormData(prev => ({
                                                        ...prev,
                                                        limits: { ...prev.limits, maxRepliesPerQuestion: parseInt(e.target.value) }
                                                    }))}
                                                />
                                            </div>
                                            <div className="form-row">
                                                <label htmlFor="plan-limits-qa-retention">Q&A Retention (days)</label>
                                                <Input
                                                    id="plan-limits-qa-retention"
                                                    type="number"
                                                    value={planFormData.limits.qaRetentionDays ?? 30}
                                                    onChange={(e) => setPlanFormData(prev => ({
                                                        ...prev,
                                                        limits: { ...prev.limits, qaRetentionDays: parseInt(e.target.value) }
                                                    }))}
                                                />
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Q&A Features Section (Webinar only) */}
                            {editingPlan.productType === 'webinar' && planFormData.features?.qa && (
                                <div className="plan-section">
                                    <button
                                        className="section-header"
                                        onClick={() => toggleSection('qaFeatures')}
                                    >
                                        <MessageSquare size={18} />
                                        <span>Q&A Features</span>
                                        {expandedSections.qaFeatures ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                                    </button>
                                    {expandedSections.qaFeatures && (
                                        <div className="section-content qa-features-grid">
                                            <label className="checkbox-label">
                                                <input
                                                    type="checkbox"
                                                    checked={planFormData.features.qa.qaEnabled || false}
                                                    onChange={(e) => setPlanFormData(prev => ({
                                                        ...prev,
                                                        features: { ...prev.features, qa: { ...prev.features.qa, qaEnabled: e.target.checked } }
                                                    }))}
                                                />
                                                Q&A Enabled
                                            </label>
                                            <label className="checkbox-label">
                                                <input
                                                    type="checkbox"
                                                    checked={planFormData.features.qa.moderationEnabled || false}
                                                    onChange={(e) => setPlanFormData(prev => ({
                                                        ...prev,
                                                        features: { ...prev.features, qa: { ...prev.features.qa, moderationEnabled: e.target.checked } }
                                                    }))}
                                                />
                                                Moderation
                                            </label>
                                            <label className="checkbox-label">
                                                <input
                                                    type="checkbox"
                                                    checked={planFormData.features.qa.repliesEnabled || false}
                                                    onChange={(e) => setPlanFormData(prev => ({
                                                        ...prev,
                                                        features: { ...prev.features, qa: { ...prev.features.qa, repliesEnabled: e.target.checked } }
                                                    }))}
                                                />
                                                Replies
                                            </label>
                                            <label className="checkbox-label">
                                                <input
                                                    type="checkbox"
                                                    checked={planFormData.features.qa.downvotesEnabled || false}
                                                    onChange={(e) => setPlanFormData(prev => ({
                                                        ...prev,
                                                        features: { ...prev.features, qa: { ...prev.features.qa, downvotesEnabled: e.target.checked } }
                                                    }))}
                                                />
                                                Downvotes
                                            </label>
                                            <label className="checkbox-label">
                                                <input
                                                    type="checkbox"
                                                    checked={planFormData.features.qa.anonymousQuestionsEnabled || false}
                                                    onChange={(e) => setPlanFormData(prev => ({
                                                        ...prev,
                                                        features: { ...prev.features, qa: { ...prev.features.qa, anonymousQuestionsEnabled: e.target.checked } }
                                                    }))}
                                                />
                                                Anonymous Questions
                                            </label>
                                            <label className="checkbox-label">
                                                <input
                                                    type="checkbox"
                                                    checked={planFormData.features.qa.labelsEnabled || false}
                                                    onChange={(e) => setPlanFormData(prev => ({
                                                        ...prev,
                                                        features: { ...prev.features, qa: { ...prev.features.qa, labelsEnabled: e.target.checked } }
                                                    }))}
                                                />
                                                Labels
                                            </label>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Active Status */}
                            <div className="plan-section status-section">
                                <div className="form-row checkbox-row">
                                    <label className="status-toggle">
                                        <input
                                            type="checkbox"
                                            checked={planFormData.active ?? true}
                                            onChange={(e) => setPlanFormData(prev => ({
                                                ...prev,
                                                active: e.target.checked
                                            }))}
                                        />
                                        <span>Plan Active</span>
                                    </label>
                                </div>
                            </div>

                            {/* Reason — the audited write records it in the plan's change
                                history next to the admin who made it. */}
                            <div className="plan-section plan-reason-section">
                                <div className="plan-reason">
                                    <label htmlFor="plan-edit-reason">Reason for the change</label>
                                    <textarea
                                        id="plan-edit-reason"
                                        value={editReason}
                                        onChange={(e) => setEditReason(e.target.value)}
                                        placeholder="Example: raising the webinar Q&A ceiling for the enterprise pilot."
                                        /* Without this the bounds are visible text only: a screen-reader user hears
                                           the label, then meets a disabled Save with nothing explaining why. */
                                        aria-describedby="plan-edit-reason-hint"
                                    />
                                    <small id="plan-edit-reason-hint">
                                        Required, 5 to 500 characters. Saved to the change history with your name.
                                    </small>
                                </div>
                            </div>
                        </div>

                        <div className="modal-actions">
                            <Button variant="ghost" onClick={() => setEditingPlan(null)}>
                                Cancel
                            </Button>
                            <Button variant="primary" onClick={handleUpdatePlan} disabled={!canSavePlan()}>
                                Save Changes
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* View Plan Details Modal */}
            {viewingPlan && (
                <PlanDetailsModal
                    plan={viewingPlan}
                    onClose={() => setViewingPlan(null)}
                    onEdit={readOnly ? null : (plan) => {
                        setViewingPlan(null);
                        openEditModal(plan);
                    }}
                    formatPrice={formatPrice}
                    getPlanDisplayName={getPlanDisplayName}
                />
            )}
        </div>
    );
};

// Plan Card Component
const PlanCard = ({ plan, productType, tier, onEdit, onView, formatPrice, getPlanDisplayName }) => {
    const isAll = productType === 'all';
    const isWebinar = productType === 'webinar';
    const ProductIcon = isAll ? Globe : (isWebinar ? Radio : Video);
    const productLabel = isAll ? 'Meeting + Webinar' : (isWebinar ? 'Webinar' : 'Meeting');

    if (!plan) {
        return null;
    }

    // Derive version badge
    const versionBadge = plan.id?.includes('_v2') ? 'v2' : null;

    const monthlyPrice = plan.monthlyPriceCents ? formatPrice(plan.monthlyPriceCents) + '/mo' : null;

    // Participant line mirrors ParticipantLimitService.cs:129-148,299-301 exactly, so the card
    // never promises a limit the server doesn't actually enforce. Reads `plan.productType` (the
    // raw catalog field), NOT the `productType` prop above (which is only this card's tier-slot
    // bucket in the admin grid and is compared case-sensitively there).
    const limits = plan.limits || {};
    const meetingCap = limits.maxMeetingParticipantsPerSession;
    const attendeeCap = limits.maxParticipantsPerSession;
    const isWebinarAttendeeCap = meetingCap == null
        && (plan.productType || '').toLowerCase() === 'webinar'
        && attendeeCap > 0;

    let participantsText;
    if (isWebinarAttendeeCap) {
        participantsText = `${attendeeCap} attendees`;
    } else if (meetingCap == null) {
        // Mirrors the server's fail-OPEN when a stored plan predates this field
        // (ParticipantLimitService.cs:144-147) — an honest "not set", not an invented number.
        participantsText = 'Participants: not set (joins are not limited)';
    } else {
        const base = meetingCap === -1 ? 'Unlimited participants' : `${meetingCap} included participants`;
        const hardCap = limits.maxMeetingParticipantsHardCap;
        // Mirrors TryApplyMeetingHardCap (ParticipantLimitService.cs:321-339): a hard cap that is
        // null, or that doesn't actually raise the ceiling (<= the included count — e.g. Free's
        // authored 0/0), is never in force server-side, so it must not claim an overflow band here.
        const hardCapRaisesCeiling = hardCap != null && meetingCap !== -1 && (hardCap === -1 || hardCap > meetingCap);
        if (!hardCapRaisesCeiling) {
            participantsText = base;
        } else {
            const capText = hardCap === -1 ? 'unlimited overflow cap' : `hard cap ${hardCap} when overflow metering is on`;
            participantsText = `${base} · ${capText}`;
        }
    }

    const languagesText = `${limits.maxTargetLanguages} languages`;

    return (
        <div className={`plan-card ${!plan.active ? 'plan-card-inactive' : ''}`}>
            <div className="plan-card-header">
                <div className="plan-card-header-left">
                    <ProductIcon size={20} className={`product-icon product-icon-${productType}`} />
                    <span className="product-type-label">{productLabel}</span>
                    {versionBadge && (
                        <span className={`version-badge version-badge-${versionBadge}`}>
                            {versionBadge}
                        </span>
                    )}
                </div>
                {!plan.active && (
                    <span className="inactive-badge">Inactive</span>
                )}
            </div>

            <h4 className="plan-card-title">{getPlanDisplayName(plan)}</h4>

            <div className="plan-card-pricing-area">
                {monthlyPrice && (
                    <div className="plan-card-pricing">
                        <div className="price-row">
                            <span className="price-label">Subscription</span>
                            <span className="price-value">{monthlyPrice}</span>
                        </div>
                    </div>
                )}
            </div>

            <div className="plan-card-limits">
                <div className="limit-item">
                    <Users size={14} />
                    <span>{participantsText}</span>
                </div>
                <div className="limit-item">
                    <Globe size={14} />
                    <span>{languagesText}</span>
                </div>
            </div>

            <div className="plan-card-footer">
                <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => onView(plan)}
                    className="view-plan-btn"
                    title="View all features"
                >
                    <Eye size={14} /> View
                </Button>
                {onEdit && (
                <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => onEdit(plan)}
                    className="edit-plan-btn"
                >
                    <Pencil size={14} /> Edit
                </Button>
                )}
            </div>
        </div>
    );
};

// Plan Details Modal Component (Read-only view)
const PlanDetailsModal = ({ plan, onClose, onEdit, formatPrice, getPlanDisplayName }) => {
    const isWebinar = plan.productType === 'webinar';
    const ProductIcon = isWebinar ? Radio : Video;

    const formatDuration = (minutes) => {
        if (!minutes) return '-';
        if (minutes < 60) return `${minutes} min`;
        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;
        return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
    };

    const FeatureItem = ({ enabled, label }) => (
        <div className={`feature-item ${enabled ? 'feature-enabled' : 'feature-disabled'}`}>
            {enabled ? <Check size={14} /> : <X size={14} />}
            <span>{label}</span>
        </div>
    );

    const DetailRow = ({ label, value, icon: Icon }) => (
        <div className="detail-row">
            {Icon && <Icon size={14} className="detail-icon" />}
            <span className="detail-label">{label}</span>
            <span className="detail-value">{value}</span>
        </div>
    );

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="plan-details-modal" onClick={e => e.stopPropagation()}>
                {/* Header */}
                <div className="plan-details-header">
                    <div className="plan-details-title-row">
                        <div className="plan-details-title-left">
                            <ProductIcon size={24} className={`product-icon product-icon-${plan.productType}`} />
                            <h3>{getPlanDisplayName(plan)}</h3>
                        </div>
                        <button className="close-btn" onClick={onClose}>
                            <X size={20} />
                        </button>
                    </div>
                    <div className="plan-details-badges">
                        <span className={`tier-label tier-label-${classSlug(plan.tier)}`}>
                            {plan.tier}
                        </span>
                        <span className={`status-indicator ${plan.active ? 'status-active' : 'status-inactive'}`}>
                            {plan.active ? 'Active' : 'Inactive'}
                        </span>
                    </div>
                    <p className="plan-id-small">ID: {plan.id}</p>
                </div>

                {/* Content */}
                <div className="plan-details-content">
                    {/* Hour Pass Section */}
                    {plan.hourPass && (
                        <div className="details-section">
                            <h4 className="section-title">
                                <Zap size={16} /> Hour Pass
                            </h4>
                            <div className="details-grid">
                                <DetailRow
                                    label="Price"
                                    value={formatPrice(plan.hourPass.priceCents) || '-'}
                                />
                                <DetailRow
                                    icon={Clock}
                                    label="Duration"
                                    value={formatDuration(plan.hourPass.durationMinutes)}
                                />
                                <DetailRow
                                    icon={Users}
                                    label="Max Participants"
                                    value={plan.hourPass.maxParticipants || '-'}
                                />
                                <DetailRow
                                    icon={Globe}
                                    label="Base Languages"
                                    value={plan.hourPass.baseLanguages || '-'}
                                />
                                <DetailRow
                                    icon={Volume2}
                                    label="Text-to-Speech"
                                    value={plan.hourPass.ttsIncluded ? 'Included' : 'Not included'}
                                />
                            </div>
                        </div>
                    )}

                    {/* Event Pass Section (Webinar only) */}
                    {isWebinar && plan.eventPass && (
                        <div className="details-section">
                            <h4 className="section-title">
                                <Zap size={16} /> Event Pass
                            </h4>
                            <div className="details-grid">
                                <DetailRow
                                    label="Price"
                                    value={formatPrice(plan.eventPass.priceCents) || '-'}
                                />
                                <DetailRow
                                    icon={Clock}
                                    label="Duration"
                                    value={formatDuration(plan.eventPass.durationMinutes)}
                                />
                                <DetailRow
                                    icon={Users}
                                    label="Max Participants"
                                    value={plan.eventPass.maxParticipants || '-'}
                                />
                                <DetailRow
                                    icon={MessageSquare}
                                    label="Q&A"
                                    value={plan.eventPass.qaIncluded ? 'Included' : 'Not included'}
                                />
                            </div>
                        </div>
                    )}

                    {/* Q&A Limits (Webinar only) */}
                    {isWebinar && plan.limits && (
                        <div className="details-section">
                            <h4 className="section-title">
                                <Database size={16} /> Q&A Limits
                            </h4>
                            <div className="details-grid">
                                <DetailRow
                                    label="Max Questions/Session"
                                    value={plan.limits.maxQuestionsPerSession === -1 ? 'Unlimited' : plan.limits.maxQuestionsPerSession || '-'}
                                />
                                <DetailRow
                                    label="Max Replies/Question"
                                    value={plan.limits.maxRepliesPerQuestion || '-'}
                                />
                                <DetailRow
                                    label="Retention Period"
                                    value={plan.limits.qaRetentionDays ? `${plan.limits.qaRetentionDays} days` : '-'}
                                />
                            </div>
                        </div>
                    )}

                    {/* Q&A Features (Webinar only) */}
                    {isWebinar && plan.features?.qa && (
                        <div className="details-section">
                            <h4 className="section-title">
                                <MessageSquare size={16} /> Q&A Features
                            </h4>
                            <div className="features-grid">
                                <FeatureItem enabled={plan.features.qa.qaEnabled} label="Q&A Enabled" />
                                <FeatureItem enabled={plan.features.qa.moderationEnabled} label="Moderation" />
                                <FeatureItem enabled={plan.features.qa.repliesEnabled} label="Replies" />
                                <FeatureItem enabled={plan.features.qa.downvotesEnabled} label="Downvotes" />
                                <FeatureItem enabled={plan.features.qa.anonymousQuestionsEnabled} label="Anonymous Questions" />
                                <FeatureItem enabled={plan.features.qa.labelsEnabled} label="Labels" />
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="plan-details-footer">
                    <Button variant="ghost" onClick={onClose}>
                        Close
                    </Button>
                    {onEdit && (
                    <Button variant="primary" onClick={() => onEdit(plan)}>
                        <Pencil size={14} /> Edit Plan
                    </Button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PlansManagement;
